import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "step", "completion", "footer",
    "progressSegment", "stepCounter",
    "backButton", "nextButton", "submitButton",
    "themeInput", "themeCard",
    "themeError", "guestCountError", "guestCountInput", "budgetInput", "budgetError", "categoryError", "visionError",
    "cityInput", "cityLabel",
    "categoryCheckbox", "categoryCount",
    "aiPrompt", "hintPills",
    "summaryTheme", "summaryDate", "summaryLocation",
    "summaryGuests", "summaryBudget", "summaryServices",
    "summaryVision", "summaryVisionContainer",
  ]
  static values = { totalSteps: Number }

  connect() {
    this.currentStep = 1
    this.updateUI()
    this.updateCategoryCount()
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  goNext() {
    if (!this.validateCurrentStep()) return

    if (this.currentStep < this.totalStepsValue) {
      this.currentStep++
      this.updateUI()
    } else {
      // Last step → show completion
      this.showCompletion()
    }
  }

  goBack() {
    if (this.isOnCompletion) {
      this.isOnCompletion = false
      this.currentStep = this.totalStepsValue
      this.footerTarget.classList.remove('hidden')
      this.completionTarget.classList.add('hidden')
      this.stepTargets.forEach(el => {
        el.classList.toggle('hidden', parseInt(el.dataset.step) !== this.currentStep)
      })
      this.updateNavigationButtons()
      this.updateProgressBar()
      this.updateStepCounter()
      return
    }

    if (this.currentStep > 1) {
      this.currentStep--
      this.updateUI()
    }
  }


  submit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = '<i class="ph-fill ph-spinner ph-spin mr-2"></i>Generating…'
    }
  }

  // ─── Completion ───────────────────────────────────────────────────────────

  showCompletion() {
    this.isOnCompletion = true
    this.stepTargets.forEach(el => el.classList.add('hidden'))
    this.completionTarget.classList.remove('hidden')
    this.footerTarget.classList.add('hidden')
    this.updateSummary()
  }

  updateSummary() {
    // Theme
    const theme = this.themeInputTarget.value
    if (this.hasSummaryThemeTarget) {
      this.summaryThemeTarget.textContent = theme || '—'
    }

    // Date
    const dateInput = this.element.querySelector('input[name="event[event_date]"]')
    if (this.hasSummaryDateTarget) {
      if (dateInput && dateInput.value) {
        const d = new Date(dateInput.value + 'T00:00:00')
        this.summaryDateTarget.textContent = d.toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
      } else {
        this.summaryDateTarget.textContent = 'TBD'
      }
    }

    // Location
    if (this.hasSummaryLocationTarget) {
      const cityLabel = this.cityLabelTarget.textContent
      this.summaryLocationTarget.textContent = cityLabel === 'No location selected' ? 'TBD' : cityLabel
    }

    // Guests
    if (this.hasSummaryGuestsTarget) {
      const count = this.hasGuestCountInputTarget ? this.guestCountInputTarget.value : ''
      this.summaryGuestsTarget.textContent = count ? `${count} guests` : '—'
    }

    // Budget
    if (this.hasSummaryBudgetTarget) {
      const budget = this.hasBudgetInputTarget ? this.budgetInputTarget.value : ''
      if (budget) {
        const formatted = new Intl.NumberFormat('en-PH').format(budget)
        this.summaryBudgetTarget.textContent = `₱${formatted}`
      } else {
        this.summaryBudgetTarget.textContent = 'Flexible'
      }
    }

    // Services
    if (this.hasSummaryServicesTarget) {
      const count = this.selectedCategoryCount()
      this.summaryServicesTarget.textContent = `${count} service${count !== 1 ? 's' : ''}`
    }

    // Vision
    if (this.hasSummaryVisionTarget && this.hasSummaryVisionContainerTarget) {
      const prompt = this.hasAiPromptTarget ? this.aiPromptTarget.value : ''
      if (prompt.trim()) {
        this.summaryVisionContainerTarget.classList.remove('hidden')
        const excerpt = prompt.length > 120 ? prompt.substring(0, 120) + '…' : prompt
        this.summaryVisionTarget.textContent = `"${excerpt}"`
      } else {
        this.summaryVisionContainerTarget.classList.add('hidden')
      }
    }
  }

  // ─── Theme selection ──────────────────────────────────────────────────────

  selectTheme(event) {
    const btn = event.currentTarget
    const theme = btn.dataset.theme

    // Update hidden input
    this.themeInputTarget.value = theme

    // Update card visuals
    this.themeCardTargets.forEach(card => {
      const isSelected = card.dataset.theme === theme
      card.classList.toggle('border-teal-500', isSelected)
      card.classList.toggle('bg-teal-50', isSelected)
      card.classList.toggle('border-gray-200', !isSelected)
      card.classList.toggle('bg-white', !isSelected)
      card.querySelector('.wizard-theme-icon')?.classList.toggle('text-teal-600', isSelected)
      card.querySelector('.wizard-theme-icon')?.classList.toggle('text-gray-400', !isSelected)
      const check = card.querySelector('.wizard-check')
      if (check) check.classList.toggle('hidden', !isSelected)
    })

    // Hide validation error
    if (this.hasThemeErrorTarget) this.themeErrorTarget.classList.add('hidden')

    // Pre-select categories for this theme
    this.preselectCategories(theme)

    // Update hint pills for step 5
    this.updateHintPills(theme)
  }

  preselectCategories(theme) {
    const defaults = this.defaultCategoriesForTheme(theme)
    this.categoryCheckboxTargets.forEach(cb => {
      const shouldCheck = defaults.includes(cb.value)
      cb.checked = shouldCheck
      this.updateCategoryCardVisual(cb)
    })
    this.updateCategoryCount()
  }

  defaultCategoriesForTheme(theme) {
    const map = {
      'Wedding':     ['Venues', 'Catering', 'Photography', 'Videography', 'Flowers & Decoration', 'Event Planning and Coordination', 'Hair & Makeup', 'Hosts & Emcees'],
      'Birthday':    ['Venues', 'Catering', 'Photography', 'Hosts & Emcees'],
      'Corporate':   ['Venues', 'Catering', 'Photography', 'Hosts & Emcees', 'Lights & Sounds'],
      'Debut':       ['Venues', 'Catering', 'Photography', 'Videography', 'Flowers & Decoration', 'Event Planning and Coordination', 'Hosts & Emcees', 'Hair & Makeup'],
      'Baptism':     ['Venues', 'Catering', 'Photography'],
      'Graduation':  ['Venues', 'Catering', 'Photography', 'Hosts & Emcees'],
      'Anniversary': ['Venues', 'Catering', 'Photography', 'Flowers & Decoration'],
      'Other':       ['Venues', 'Catering'],
    }
    return map[theme] || ['Venues', 'Catering']
  }

  updateHintPills(theme) {
    if (!this.hasHintPillsTarget) return

    const hints = this.hintsForTheme(theme)
    const container = this.hintPillsTarget
    container.innerHTML = hints.map(hint => `
      <button type="button"
              class="hint-pill text-xs px-3 py-1.5 bg-gray-100 hover:bg-teal-100 hover:text-teal-700 rounded-full text-gray-600 transition-colors cursor-pointer"
              data-action="click->wizard#applyHint"
              data-hint="${hint.replace(/"/g, '&quot;')}">
        ${hint}
      </button>
    `).join('')
  }

  applyHint(event) {
    const hint = event.currentTarget.dataset.hint
    if (this.hasAiPromptTarget) {
      const current = this.aiPromptTarget.value
      this.aiPromptTarget.value = current ? `${current} ${hint}` : hint
      this.aiPromptTarget.dispatchEvent(new Event('input'))
    }
  }

  hintsForTheme(theme) {
    const map = {
      'Wedding':     ['Elegant garden ceremony', 'Traditional Filipino wedding', 'Intimate 50-person celebration', 'Modern minimalist theme'],
      'Birthday':    ['Surprise party setup', 'Garden party theme', 'Elegant adult celebration', 'Colorful kids party'],
      'Corporate':   ['Product launch event', 'Annual company party', 'Team-building outing', 'Awards night ceremony'],
      'Debut':       ['Princess-themed debut', 'Garden debut party', 'Elegant ballroom setting', 'Modern vintage theme'],
      'Baptism':     ['Intimate family gathering', 'Garden baptism reception', 'Classic white and gold theme'],
      'Graduation':  ['Intimate family dinner', 'Class reunion party', 'Outdoor garden celebration'],
      'Anniversary': ['Intimate dinner party', 'Vow renewal ceremony', 'Silver jubilee celebration'],
      'Other':       ['Casual get-together', 'Themed party celebration', 'Milestone celebration'],
    }
    return map[theme] || ['Tell us more about your event...']
  }

  // ─── City selection ───────────────────────────────────────────────────────

  selectCity(event) {
    const btn = event.currentTarget
    const cityId = btn.dataset.cityId
    const cityName = btn.dataset.cityName

    // Toggle selection
    const isSelected = this.cityInputTarget.value === cityId

    if (isSelected) {
      // Deselect
      this.cityInputTarget.value = ''
      this.cityLabelTarget.textContent = 'No location selected'
      btn.classList.remove('border-teal-500', 'bg-teal-50', 'text-teal-700')
      btn.classList.add('border-gray-200', 'bg-gray-50')
    } else {
      // Select new city
      this.cityInputTarget.value = cityId
      this.cityLabelTarget.textContent = cityName

      // Update all pills
      this.element.querySelectorAll('.city-pill').forEach(pill => {
        const selected = pill.dataset.cityId === cityId
        pill.classList.toggle('border-teal-500', selected)
        pill.classList.toggle('bg-teal-50', selected)
        pill.classList.toggle('text-teal-700', selected)
        pill.classList.toggle('border-gray-200', !selected)
        pill.classList.toggle('bg-gray-50', !selected)
      })
    }
  }

  // ─── Category selection ───────────────────────────────────────────────────

  updateCategoryCount() {
    const count = this.selectedCategoryCount()
    if (this.hasCategoryCountTarget) {
      this.categoryCountTarget.textContent = `${count} selected`
    }

    // Update category card visuals
    this.categoryCheckboxTargets.forEach(cb => this.updateCategoryCardVisual(cb))
  }

  updateCategoryCardVisual(checkbox) {
    const card = checkbox.closest('.category-card')
    if (!card) return

    card.classList.toggle('border-teal-500', checkbox.checked)
    card.classList.toggle('bg-teal-50', checkbox.checked)
    card.classList.toggle('border-gray-200', !checkbox.checked)

    const icon = card.querySelector('.wizard-cat-icon')
    if (icon) {
      icon.classList.toggle('text-teal-600', checkbox.checked)
      icon.classList.toggle('text-gray-400', !checkbox.checked)
    }

    const check = card.querySelector('.wizard-check')
    if (check) check.classList.toggle('hidden', !checkbox.checked)
  }

  selectedCategoryCount() {
    return this.categoryCheckboxTargets.filter(cb => cb.checked).length
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  validateCurrentStep() {
    if (this.currentStep === 1) {
      if (!this.themeInputTarget.value) {
        if (this.hasThemeErrorTarget) this.themeErrorTarget.classList.remove('hidden')
        return false
      }
    }

    if (this.currentStep === 3) {
      const guestVal = this.hasGuestCountInputTarget ? parseInt(this.guestCountInputTarget.value) : 0
      if (!guestVal || guestVal < 1) {
        if (this.hasGuestCountErrorTarget) this.guestCountErrorTarget.classList.remove('hidden')
        return false
      }
      if (this.hasGuestCountErrorTarget) this.guestCountErrorTarget.classList.add('hidden')

      const budgetVal = this.hasBudgetInputTarget ? parseInt(this.budgetInputTarget.value) : 0
      if (!budgetVal || budgetVal < 1) {
        if (this.hasBudgetErrorTarget) this.budgetErrorTarget.classList.remove('hidden')
        return false
      }
      if (this.hasBudgetErrorTarget) this.budgetErrorTarget.classList.add('hidden')
    }

    if (this.currentStep === 4) {
      if (this.selectedCategoryCount() === 0) {
        if (this.hasCategoryErrorTarget) this.categoryErrorTarget.classList.remove('hidden')
        return false
      }
      if (this.hasCategoryErrorTarget) this.categoryErrorTarget.classList.add('hidden')
    }

    if (this.currentStep === 5) {
      const prompt = this.hasAiPromptTarget ? this.aiPromptTarget.value.trim() : ''
      if (prompt.length < 10) {
        if (this.hasVisionErrorTarget) this.visionErrorTarget.classList.remove('hidden')
        return false
      }
      if (this.hasVisionErrorTarget) this.visionErrorTarget.classList.add('hidden')
    }

    return true
  }

  // ─── UI Updates ───────────────────────────────────────────────────────────

  updateUI() {
    this.stepTargets.forEach(el => {
      el.classList.toggle('hidden', parseInt(el.dataset.step) !== this.currentStep)
    })
    this.updateProgressBar()
    this.updateStepCounter()
    this.updateNavigationButtons()
  }

  updateProgressBar() {
    if (!this.hasProgressSegmentTarget) return
    this.progressSegmentTargets.forEach(seg => {
      const idx = parseInt(seg.dataset.segmentIndex)
      seg.style.backgroundColor = idx <= this.currentStep ? '#1a202c' : '#e5e7eb'
    })
  }

  updateStepCounter() {
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = `Step ${this.currentStep} of ${this.totalStepsValue}`
    }
  }

  updateNavigationButtons() {
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.disabled = this.currentStep === 1
    }

    if (this.hasNextButtonTarget) {
      const isLastStep = this.currentStep === this.totalStepsValue
      this.nextButtonTarget.textContent = isLastStep ? 'Review' : 'Next'
      const icon = isLastStep ? 'ph-check' : 'ph-arrow-right'
      this.nextButtonTarget.innerHTML = isLastStep
        ? `Review <i class="ph ph-check ml-1.5"></i>`
        : `Next <i class="ph ph-arrow-right ml-1.5"></i>`
    }
  }
}
